import React, { useContext, useState } from 'react';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEdit, faTrash, faChevronDown, faChevronLeft } from '@fortawesome/free-solid-svg-icons';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import rtlPlugin from 'stylis-plugin-rtl';
import { CacheProvider } from '@emotion/react';
import createCache from '@emotion/cache';
import { prefixer } from 'stylis';
import { UserContext } from '../context/UserContext';
import API from '../config/axiosConfig';
import './Hospitals.scss';
import CommonLayout from '../components/CommonLayout';
import FloatingAddButton from '../components/FloatingAddButton';
import DeleteConfirmationDialog from '../components/dialogs/DeleteConfirmationDialog';
import { TextField, Button, FormControl, InputLabel, Select, MenuItem, Checkbox, ListItemText } from '@mui/material';
import ConfirmationDialog from '../components/dialogs/ConfirmationDialog';
import {
  DUPLICATE_ENGLISH_NAME_MESSAGE,
  DUPLICATE_HEBREW_NAME_MESSAGE,
  getDepartmentNameErrors,
  getServerHebMessage,
} from '../utils/department-utils';




// Create rtl cache
const cacheRtl = createCache({
  key: 'muirtl',
  stylisPlugins: [prefixer, rtlPlugin],
});

// Create RTL theme
const theme = createTheme({ typography: { fontFamily: '"Rubik", sans-serif' },
  direction: 'rtl',
});

const Hospitals = () => {
  const [expandedHospital, setExpandedHospital] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogType, setDialogType] = useState('');
  const [editItem, setEditItem] = useState(null);
  const [nameHebrew, setNameHebrew] = useState('');
  const [nameEnglish, setNameEnglish] = useState('');
  const [deleteItem, setDeleteItem] = useState(null);
  const [deleteError, setDeleteError] = useState('');
  const [nameHebrewError, setNameHebrewError] = useState('');
  const [nameEnglishError, setNameEnglishError] = useState('');
  const [homepage, setHomepage] = useState('');
  const [attemptedSave, setAttemptedSave] = useState(false);
  const { user, hospitals, fetchHospitals, createDepartment } = useContext(UserContext);
  const [isSubmitting, setIsSubmitting] = useState(false);


  const handleExpandHospital = (id) => {
    setExpandedHospital(expandedHospital === id ? null : id);
  };

  const openDialog = (type, item = null) => {
    setDialogType(type);
    setEditItem(item);
    setNameHebrew(item ? item.nameHebrew || '' : '');
    setNameEnglish(item ? item.nameEnglish || '' : '');
    setHomepage(item ? item.homepage || '' : '');
    setDialogOpen(true);
    setAttemptedSave(false);
    setNameHebrewError('');
    setNameEnglishError('');
  };

  const closeDialog = () => {
    setDialogOpen(false);
    setNameHebrew('');
    setNameEnglish('');
    setHomepage('');
    setEditItem(null);
    setAttemptedSave(false);
    setNameHebrewError('');
    setNameEnglishError('');
  };

  const validateInput = () => {
    let isValid = true;
    setAttemptedSave(true);

    if (!nameHebrew) {
      setNameHebrewError('שדה חובה');
      isValid = false;
    } else {
      setNameHebrewError('');
    }

    if (dialogType === 'createDepartment' || dialogType === 'editDepartment') {
      const hospital = hospitals.find(h => h._id === editItem.hospital);
      const duplicateErrors = getDepartmentNameErrors(
        hospital?.departments,
        { nameHebrew, nameEnglish },
        dialogType === 'editDepartment' ? editItem : null
      );

      if (duplicateErrors.nameHebrew) {
        setNameHebrewError(duplicateErrors.nameHebrew);
        isValid = false;
      }

      if (duplicateErrors.nameEnglish) {
        setNameEnglishError(duplicateErrors.nameEnglish);
        isValid = false;
      }
    }

    return isValid;
  };

const handleSave = async () => {
  if (!validateInput()) return;

  try {
    setIsSubmitting(true); 

    const departmentData = { nameHebrew, nameEnglish, homepage };

    if (dialogType === 'editHospital' || dialogType === 'createHospital') {
      const method = dialogType === 'editHospital' ? 'put' : 'post';
      const url = dialogType === 'editHospital'
        ? `/api/hospitals/${editItem._id}`
        : '/api/hospitals';
      await API[method](url, { nameHebrew, nameEnglish });
    } else if (dialogType === 'createDepartment') {
      await createDepartment(editItem.hospital, departmentData);
    } else {
      await API.put(
        `/api/hospitals/${editItem.hospital}/departments/${editItem._id}`,
        departmentData,
        { handlingErrorLocally: true }
      );
    }

    await fetchHospitals();
    closeDialog();
  } catch (error) {
    console.error('Saving failed:', error);
    // The server rejects duplicate department names per hospital - show it on the offending field.
    const hebMessage = getServerHebMessage(error);
    if (hebMessage === DUPLICATE_HEBREW_NAME_MESSAGE) {
      setNameHebrewError(hebMessage);
    } else if (hebMessage === DUPLICATE_ENGLISH_NAME_MESSAGE) {
      setNameEnglishError(hebMessage);
    } else if (window.showFloatingError && error.serverError?.hebMessage) {
      window.showFloatingError(error.serverError);
    }
  } finally {
    setIsSubmitting(false); 
  }
};

  const openDeleteConfirm = (item, type) => {
    setDeleteItem({ ...item, type });
  };

  const handleDelete = async () => {
    try {
      if (deleteItem.type === 'hospital') {
        await API.delete(`/api/hospitals/${deleteItem._id}`);
      } else {
        await API.delete(`/api/hospitals/${deleteItem.hospital}/departments/${deleteItem._id}`);
      }
      fetchHospitals();
      setDeleteItem(null);
    } catch (error) {
      console.error('Deletion failed:', error);
      setDeleteError(error.response?.data?.message || 'Deletion failed');
    }
  };

  const canEditHospital = user && user.isSuperAdmin;
  const canAddDepartment = (hospitalId) =>
    user &&
    (user.isSuperAdmin ||
      user.roles.some(
        (role) =>
          role.hospital === hospitalId && (role.role === 'EDITOR' || role.role === 'ADMIN')
      ));
  const canEditDepartment = (departmentId) =>
    user &&
    (user.isSuperAdmin ||
      user.roles.some((role) => role.department === departmentId && role.role === 'ADMIN'));

  return (
    <CommonLayout >
      <div className="hospitals">
    <div className="hospitals-container">
      {hospitals.map((hospital) => (
        <div 
          key={hospital._id} 
          className="hospital-item"
        >
          <div className="hospital-header" onClick={() => handleExpandHospital(hospital._id)}>
            <div className="hospital-title">
              <FontAwesomeIcon
                icon={expandedHospital === hospital._id ? faChevronDown : faChevronLeft}
                className="expand-icon"
              />
              <h2 style={{color:"white"}}>{hospital.nameHebrew}</h2>
            </div>
            <div className="hospital-actions">
              {canEditHospital && (
                <>
                  <FontAwesomeIcon
                    icon={faEdit}
                    onClick={(e) => {
                      e.stopPropagation();
                      openDialog('editHospital', hospital);
                    }}
                    className="action-icon"
                    style={{color: "white"}}
                  />
                  <FontAwesomeIcon
                    icon={faTrash}
                    onClick={(e) => {
                      e.stopPropagation();
                      openDeleteConfirm(hospital, 'hospital');
                    }}
                    className="action-icon"
                    style={{color: "white"}}
                  />
                </>
              )}
            </div>
          </div>
          {expandedHospital === hospital._id && (
            <div className="departments-list">
              {hospital.departments.map((department) => (
                <div key={department._id} className="department-item">
                  <div className="department-info">
                    <span>{department.nameHebrew}</span>
                  </div>
                  {canEditDepartment(department._id) && (
                    <div className="department-actions">
                      <FontAwesomeIcon
                        icon={faEdit}
                        onClick={() => openDialog('editDepartment', { ...department, hospital: hospital._id })}
                        className="action-icon"
                      />
                      {user.isSuperAdmin && (
                        <FontAwesomeIcon
                          icon={faTrash}
                          onClick={() => openDeleteConfirm({ ...department, hospital: hospital._id }, 'department')}
                          className="action-icon"
                        />
                      )}
                    </div>
                  )}
                </div>
              ))}
              {canAddDepartment(hospital._id) && (
                <div className="add-department-container">
                  <button
                    className="add-department-btn"
                    onClick={() => openDialog('createDepartment', { hospital: hospital._id })}
                  >
                    הוסף מחלקה
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      ))}

      {canEditHospital && (
        <FloatingAddButton
          onClick={() => openDialog('createHospital')}
          entityName="בית חולים"
          dataLoaded={true}
        />
      )}

      <CacheProvider value={cacheRtl}>
        <ThemeProvider theme={theme}>
          <ConfirmationDialog
            open={dialogOpen}
            onClose={closeDialog}
            onConfirm={handleSave}
            title={dialogType.includes('Hospital') ? 'בית חולים' : 'מחלקה'}
            confirmText="שמור"
            cancelText="ביטול"
            isConfirmLoading={isSubmitting}
            confirmLoadingText={
            dialogType === 'createDepartment' ? 'מוסיף מחלקה...'
            : dialogType === 'editDepartment' ? 'שומר מחלקה...'
            : dialogType === 'createHospital' ? 'שומר...'
            : 'שומר...'
          }
          >
            <TextField
              autoFocus
              margin="dense"
              label={dialogType.includes('Hospital') ? 'שם בית החולים' : 'שם המחלקה'}
              fullWidth
              value={nameHebrew || ''}
              onChange={(e) => setNameHebrew(e.target.value)}
              error={!!nameHebrewError}
              helperText={nameHebrewError}
            />

            <TextField
              margin="dense"
              label={dialogType.includes('Hospital') ? 'שם בית החולים באנגלית' : 'שם המחלקה באנגלית'}
              fullWidth
              value={nameEnglish || ''}
              onChange={(e) => setNameEnglish(e.target.value)}
              error={!!nameEnglishError}
              helperText={nameEnglishError}
            />

            {(dialogType === 'createDepartment' || dialogType === 'editDepartment') && (
              <TextField
                margin="dense"
                label="Homepage"
                fullWidth
                value={homepage || ''}
                onChange={(e) => setHomepage(e.target.value)}
              />
              
            )}
          </ConfirmationDialog>

          <DeleteConfirmationDialog
            open={!!deleteItem}
            onClose={() => setDeleteItem(null)}
            onConfirm={handleDelete}
            title={
              deleteItem?.type === 'hospital'
                ? `האם למחוק את בית החולים ${deleteItem?.nameHebrew}?`
                : `האם למחוק את מחלקת ${deleteItem?.nameHebrew}?`
            }
            errorMessage={deleteError}
            showError={!!deleteError}
            onCloseError={() => setDeleteError('')}
          />
        </ThemeProvider>
      </CacheProvider>
    </div>
    </div>
    </CommonLayout>
  );
};

export default Hospitals;